import React from 'react';

import clsx from 'clsx';
import dayjs from 'dayjs';
import { Box, Typography, makeStyles, Theme, createStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { FiClock } from 'react-icons/fi';
import { IoCheckmark, IoCheckmarkDone } from 'react-icons/io5';

export type ChatBubbleSide = 'in' | 'out';
export type ChatBubbleStatus = 'sending' | 'sent' | 'read';

export interface ChatBubbleProps {
  // 'out' is the user's own message, on the right.
  side: ChatBubbleSide;
  // Position in a burst from one sender: only the last gets the tail.
  first: boolean;
  last: boolean;
  // Milliseconds; nothing is shown for 0.
  time?: number;
  // Delivery ticks next to the time on the user's own messages.
  status?: ChatBubbleStatus;
  edited?: boolean;
  // Group chats: who wrote it, over the first bubble of their burst.
  senderName?: string;
  // Group chats: the sender's picture beside the last bubble of their burst;
  // the space is kept for the others so the burst lines up.
  avatar?: React.ReactNode;
  // Inside the bubble above the text: "Forwarded from", a replied-to quote.
  header?: React.ReactNode;
  // Inside the bubble above the text: pictures and files.
  media?: React.ReactNode;
  // The text.
  children?: React.ReactNode;
  // Inside the bubble under the text, e.g. "Translated · Show original".
  note?: React.ReactNode;
  // Under the bubble: reactions, "couldn't send · Try again".
  below?: React.ReactNode;
  // Beside the bubble, shown on hover (always on touch screens).
  actions?: React.ReactNode;
  onDoubleClick?: () => void;
  // Right click: the message menu.
  onContextMenu?: (event: React.MouseEvent) => void;
}

const formatTime = (time: number) => dayjs(time).format('HH:mm');

const StatusIcon = ({ status }: { status: ChatBubbleStatus }) =>
  status === 'read' ? (
    <IoCheckmarkDone size={15} aria-label="Read" />
  ) : status === 'sent' ? (
    <IoCheckmark size={15} aria-label="Sent" />
  ) : (
    <FiClock size={11} aria-label="Sending" />
  );

// The message bubble shared by every chat: Telegram's grouping and tail, the
// time and ticks tucked into the last line of text, and slots for what each
// chat adds (quotes, media, reactions, actions).
export const ChatBubble: React.FC<ChatBubbleProps> = ({
  side,
  first,
  last,
  time,
  status,
  edited,
  senderName,
  avatar,
  header,
  media,
  children,
  note,
  below,
  actions,
  onDoubleClick,
  onContextMenu,
}) => {
  const classes = useStyles();
  const isOut = side === 'out';
  const hasText = children !== undefined && children !== null && children !== false && children !== '';
  const showName = !!senderName && first && !isOut;

  const stamp = (!!time || (isOut && status)) && (
    <span className={clsx(classes.stamp, { [classes.stampAlone]: !hasText })}>
      {edited && <span className={classes.edited}>edited</span>}
      {!!time && formatTime(time)}
      {isOut && status && (
        <span className={clsx(classes.status, { [classes.read]: status === 'read' })}>
          <StatusIcon status={status} />
        </span>
      )}
    </span>
  );

  return (
    <Box
      className={clsx(classes.row, isOut ? classes.rowOut : classes.rowIn, { [classes.rowFirst]: first })}
    >
      {avatar !== undefined && !isOut && (
        <Box className={classes.avatar} aria-hidden={!last}>
          {last ? avatar : null}
        </Box>
      )}

      <Box className={clsx(classes.stack, isOut ? classes.stackOut : classes.stackIn)}>
        <Box className={classes.line}>
          {isOut && actions && <Box className={classes.actions}>{actions}</Box>}

          <Box
            className={clsx(classes.bubble, isOut ? classes.out : classes.in, {
              [classes.tail]: last,
              [classes.joinedAbove]: !first,
              [classes.withMedia]: !!media,
            })}
            onDoubleClick={onDoubleClick}
            onContextMenu={onContextMenu}
          >
            {showName && (
              <Typography component="span" className={classes.name}>
                {senderName}
              </Typography>
            )}
            {header}
            {media && <Box className={classes.media}>{media}</Box>}
            {hasText ? (
              <Box className={classes.text}>
                {children}
                {stamp}
              </Box>
            ) : (
              stamp
            )}
            {note}
          </Box>

          {!isOut && actions && <Box className={classes.actions}>{actions}</Box>}
        </Box>

        {below}
      </Box>
    </Box>
  );
};

const RADIUS = 18;
const RADIUS_JOINED = 6;
// The tail is a square with a quarter circle cut out, half of it under the bubble.
const TAIL = 14;
const AVATAR = 32;

export const chatBubbleColors = (theme: Theme) => ({
  outBackground: theme.palette.type === 'dark' ? theme.palette.primary.dark : theme.palette.primary.main,
  outText: theme.palette.common.white,
  inBackground: theme.palette.background.paper,
});

const useStyles = makeStyles((theme: Theme) => {
  const { outBackground, outText, inBackground } = chatBubbleColors(theme);

  return createStyles({
    row: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: 6,
      marginTop: 2,
      maxWidth: '82%',
      animation: '$enter 280ms cubic-bezier(.2,.8,.2,1) both',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      [theme.breakpoints.down('xs')]: { maxWidth: '90%' },
    },
    rowFirst: { marginTop: 10 },
    rowIn: { alignSelf: 'flex-start' },
    rowOut: { alignSelf: 'flex-end' },
    avatar: { flex: 'none', width: AVATAR, minHeight: 1, display: 'flex', alignItems: 'flex-end' },
    stack: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
    stackIn: { alignItems: 'flex-start' },
    stackOut: { alignItems: 'flex-end' },
    line: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: 4,
      maxWidth: '100%',
      '&:hover $actions, &:focus-within $actions': { opacity: 1 },
    },
    bubble: {
      position: 'relative',
      // Contains the floated time.
      display: 'flow-root',
      minWidth: 0,
      padding: '7px 12px 8px',
      borderRadius: RADIUS,
      fontSize: 15,
      lineHeight: 1.45,
      overflowWrap: 'anywhere',
      boxShadow: `0 1px 1px ${alpha(theme.palette.text.primary, 0.08)}`,
    },
    withMedia: {
      paddingTop: 4,
      paddingLeft: 4,
      paddingRight: 4,
      '& $text, & $name, & $stampAlone': { marginLeft: 8, marginRight: 8 },
    },
    media: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 4 },
    text: { display: 'flow-root' },
    in: {
      backgroundColor: inBackground,
      color: theme.palette.text.primary,
      '&$joinedAbove': { borderTopLeftRadius: RADIUS_JOINED },
      '&:not($tail)': { borderBottomLeftRadius: RADIUS_JOINED },
      '&$tail': { borderBottomLeftRadius: 0 },
      '&$tail::before': {
        left: -TAIL / 2,
        background: inBackground,
        WebkitMask: `radial-gradient(circle at 0 0, transparent ${TAIL - 0.5}px, ${inBackground} ${TAIL}px)`,
        mask: `radial-gradient(circle at 0 0, transparent ${TAIL - 0.5}px, ${inBackground} ${TAIL}px)`,
      },
      '& $stamp': { color: theme.palette.text.secondary },
      '& a': { color: theme.palette.primary.main },
    },
    out: {
      backgroundColor: outBackground,
      color: outText,
      '&$joinedAbove': { borderTopRightRadius: RADIUS_JOINED },
      '&:not($tail)': { borderBottomRightRadius: RADIUS_JOINED },
      '&$tail': { borderBottomRightRadius: 0 },
      '&$tail::before': {
        right: -TAIL / 2,
        background: outBackground,
        WebkitMask: `radial-gradient(circle at 100% 0, transparent ${
          TAIL - 0.5
        }px, ${outBackground} ${TAIL}px)`,
        mask: `radial-gradient(circle at 100% 0, transparent ${TAIL - 0.5}px, ${outBackground} ${TAIL}px)`,
      },
      '& $stamp': { color: alpha(outText, 0.75) },
      '& a': { color: outText, textDecoration: 'underline' },
      '& code': { backgroundColor: alpha(outText, 0.16) },
    },
    tail: {
      '&::before': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        width: TAIL,
        height: TAIL,
      },
    },
    joinedAbove: {},
    name: {
      display: 'block',
      fontSize: 13,
      fontWeight: 600,
      lineHeight: '18px',
      color: theme.palette.primary.main,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    // Floats into the last line of text, like Telegram's timestamp.
    stamp: {
      float: 'right',
      position: 'relative',
      top: 6,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 3,
      marginLeft: 10,
      fontSize: 11,
      lineHeight: '16px',
      whiteSpace: 'nowrap',
      userSelect: 'none',
    },
    // Without text the time sits on its own line under the media.
    stampAlone: { float: 'none', top: 0, display: 'flex', justifyContent: 'flex-end', marginLeft: 0 },
    edited: { fontStyle: 'italic' },
    status: { display: 'inline-flex' },
    read: {},
    actions: {
      flex: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      opacity: 0,
      transition: 'opacity 150ms',
      '@media (hover: none)': { opacity: 1 },
    },
    '@keyframes enter': {
      from: { opacity: 0, transform: 'translateY(8px)' },
      to: { opacity: 1, transform: 'none' },
    },
  });
});
